import Enterprise24 from '@carbon/icons-react/es/enterprise/24';
import Task16 from '@carbon/icons-react/es/task/16';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Project } from 'api/project';
import { Avatar } from 'components/atoms/avatar';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover } from 'components/atoms/popover';
import { Option, Select } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { isRelatedEl } from 'components/molecules/new-task-popup/is-related-element';
import { useGetProjectIdsOfCurrentUserFromIndexedDb } from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import { TimesheetTableTimeEntry, TimesheetWeeklyRowTask } from '../../types';
import { ProjectOption } from '../project-option';
import { getMinutes } from '../time-entry-utils';
import { DropdownIndicator } from '../title-select';
import { getProjectSelectOptions } from '../utils';
import { NameCellWithTooltip } from './name-cell';
import { SelectContainer, selectStyles, WrapperCell } from './task-name-cell';

const StatusIndicatorWrapper = styled.span`
  svg {
    width: 24px;
    height: 24px;
  }
`;

export const ProjectNameCell: FC<{
  task: TimesheetWeeklyRowTask;
  projects: Project[] | undefined;
  onActivate?: (isActive: boolean) => void;
  days: Record<string, TimesheetTableTimeEntry[]>;
  projectOptions?: Option[];
  changeProject?: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    projectToBeAdded: Option,
  ) => void;
}> = ({ task, projects, days, onActivate, projectOptions, changeProject }) => {
  const translate = useTranslate();
  const myProjectIds = useGetProjectIdsOfCurrentUserFromIndexedDb();
  const project = projects?.find(
    (x: Project) => x.projectId === task.project?.id,
  );

  const [visible, setVisible] = useState(false);

  const handleVisibilityToggle = useCallback((currentVisibility: boolean) => {
    setVisible(currentVisibility);
  }, []);

  const onSelection = useCallback(
    (props) => {
      changeProject?.(task, days, props);
      handleVisibilityToggle(false);
    },
    [changeProject, days, handleVisibilityToggle, task],
  );

  const minutes = getMinutes(days);

  const projectSelectOptions = getProjectSelectOptions(
    projectOptions,
    myProjectIds,
    translate,
  );

  useEffect(() => onActivate?.(visible), [onActivate, visible]);

  const openProjectPopup = useCallback(() => {
    if (minutes === 0) {
      setVisible(true);
    }
  }, [minutes]);

  const onBlurHandler = useCallback(
    () => handleVisibilityToggle(false),
    [handleVisibilityToggle],
  );

  return (
    <OutsideClickHandler
      allowed={isRelatedEl}
      onOutsideClick={() => handleVisibilityToggle(false)}
    >
      <Popover
        placement="bottomLeft"
        visible={visible}
        content={
          <SelectContainer data-cy="timesheet-project-select">
            <Select
              type="user"
              menuIsOpen
              autoFocus
              components={{
                ClearIndicator: null,
                IndicatorSeparator: null,
                Option: ProjectOption,
                MenuList: VirtualizedMenuList,
                DropdownIndicator,
              }}
              isClearable
              placeholder={<FormattedMessage id="placeholder.search" />}
              tabSelectsValue={false}
              styles={selectStyles}
              options={projectSelectOptions ?? []}
              onChange={onSelection}
              onBlur={onBlurHandler}
              grouped
              noOptionsMessage={() => (
                <FormattedMessage id="projects.no_results.empty.title" />
              )}
            />
          </SelectContainer>
        }
      >
        <WrapperCell onClick={openProjectPopup} isNoEntriesSet={minutes === 0}>
          <NameCellWithTooltip
            logo={
              task.project?.id === 1 || !task.project?.id ? (
                <Enterprise24 />
              ) : (
                <StatusIndicatorWrapper>
                  <Avatar
                    src={project?.customer.companyLogoUrl}
                    name={task.project?.name}
                    icon={<Task16 />}
                  />
                </StatusIndicatorWrapper>
              )
            }
            primaryText={
              task.project?.name ? (
                task.project?.name
              ) : (
                <FormattedMessage id="timesheets.labels.non_project_activity" />
              )
            }
          />
        </WrapperCell>
      </Popover>
    </OutsideClickHandler>
  );
};
